import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { JUDGE_POSITIONS } from "score-common";
import { useTranslation } from "i18n-fe";
import { useActiveOrg, useActiveShow, TableSkeleton, userDisplayName } from "common-fe";
// #1805 — formatStartTime's `v` is scheduledTime, an FVD Access stamp
// (#1811), not a genuine instant, so it renders through the shared
// FVD_STAMP_TIMEZONE constant, not a local "America/Los_Angeles" literal.
import { FVD_STAMP_TIMEZONE } from "common-fe/venueTime";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useAccountApi } from "../api/accountApi";
import { classNumberLabel, sortByClassNumber, classDay, classDayKey } from "../util/classDisplay";
import ClassDetailDrawer from "../components/ClassDetailDrawer";
import ClassLink from "../components/ClassLink";
import FvdPushStatusIcon from "../components/FvdPushStatusIcon";
import BulkAssignScribesDialog from "../components/BulkAssignScribesDialog";
import { useClassesNeedingPlacings } from "../hooks/useClassesNeedingPlacings";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useTestDisagreementRideTests } from "../hooks/useTestDisagreementRideTests";
import TestDisagreementChip from "../components/TestDisagreementChip";

// #413 wire form: the grantable scribe role qualified for the token.
const SCRIBE_ROLE = "score:score_scribe";

// The judge-booth positions worth showing a scribe select for on a class: ONLY
// the ones the class actually staffs (from FVD's judge panel, #555).
function positionsForClass(classItem) {
  return JUDGE_POSITIONS.filter(
    (p) => Array.isArray(classItem.judgeAssignments?.[p]) && classItem.judgeAssignments[p].length,
  );
}

// #573 — a class's scheduled start time for the list column. scheduledTime is
// an FVD-stamped string; render an ISO datetime as a short local time, and
// leave an already-formatted string ("8:30 AM") untouched.
function formatStartTime(v) {
  if (!v) return null;
  const d = new Date(v);
  if (!Number.isNaN(d.getTime()) && /\d{4}-\d{2}-\d{2}/.test(v)) {
    // #612 / #1805 — render the FVD stamp through FVD_STAMP_TIMEZONE.
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZone: FVD_STAMP_TIMEZONE });
  }
  return String(v);
}

// A one-line summary of a class's scribe assignments for the list row, e.g.
// "C: Jane · H: Bob". Empty string when nothing is assigned.
function scribeSummary(classItem, scribeLabel) {
  return positionsForClass(classItem)
    .map((p) => {
      const ids = classItem.scribeAssignments?.[p];
      const id = Array.isArray(ids) && ids.length ? ids[0] : null;
      return id ? `${p}: ${scribeLabel(id)}` : null;
    })
    .filter(Boolean)
    .join(" · ");
}

// #1062 — a one-line summary of a class's FVD-reconciled judge panel for the
// list row, e.g. "C: Jane Smith · H: Bob Jones". Reads judgeAssignments
// directly (plain FVD name strings, not roster ids — no lookup needed).
// Empty string when the class has no judge panel yet.
function judgeSummary(classItem) {
  return Object.entries(classItem.judgeAssignments || {})
    .filter(([, names]) => Array.isArray(names) && names.length)
    .map(([position, names]) => `${position}: ${names.filter(Boolean).join(", ")}`)
    .join(" · ");
}

// #656 — the judged positions that still have no scribe, e.g. ["B"]. Empty
// array when every judged position is covered (or there are none to cover).
function missingScribePositions(classItem) {
  return positionsForClass(classItem).filter(
    (p) => !(Array.isArray(classItem.scribeAssignments?.[p]) && classItem.scribeAssignments[p].length),
  );
}

// #593 — a class is "missing a scribe" only when it HAS judged positions (so it
// needs scribing) and at least one of them has no scribe assigned. Classes with
// no judge panel can't be scribed, so they are NOT flagged — otherwise the
// filter/label matches nearly every class and looks like it does nothing.
function isMissingScribe(classItem) {
  const positions = positionsForClass(classItem);
  return (
    positions.length > 0 &&
    positions.some(
      (p) =>
        !(Array.isArray(classItem.scribeAssignments?.[p]) && classItem.scribeAssignments[p].length),
    )
  );
}

export default function ClassManager() {
  const { t } = useTranslation("scoreadmin");
  const navigate = useNavigate();
  // #863 — the URL is the source of truth for which class's detail drawer is
  // open (mirrors /rides/:rideId, App.jsx). No local selectedClassId state:
  // selecting a row or closing the drawer navigates, and the drawer's own
  // open state just falls out of whether classId resolves to a known class
  // (see selectedClass below) — an unknown/stale id degrades to "closed"
  // rather than throwing.
  const { classId: selectedClassId = null } = useParams();
  const { activeOrgId } = useActiveOrg();
  const { activeShowId, loading: loadingShow } = useActiveShow();
  const scoreApi = useScoreApi();
  const accountApi = useAccountApi();

  const [classes, setClasses] = useState([]);
  const [scribes, setScribes] = useState([]);
  const [judges, setJudges] = useState([]);
  const [testsById, setTestsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // #553 — list controls + the selected class for the detail drawer.
  const [search, setSearch] = useState("");
  const [groupBy, setGroupBy] = useState("ring"); // none | day | ring | day-ring
  const [sortBy, setSortBy] = useState("number"); // number | name | time
  // #575 — show only classes with no scribe assigned to any judged position.
  const [noScribeOnly, setNoScribeOnly] = useState(false);
  // #340 — show only classes that are scored but have no placings recorded.
  const [needsPlacingsOnly, setNeedsPlacingsOnly] = useState(false);
  // #574 — per-group collapse state; a key present here is collapsed.
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());

  // #1055 — Gmail-style bulk selection: a Set of selected class ids. Kept
  // independent of the filter/group state above — selecting some rows and
  // then narrowing the filter doesn't silently drop the selection, it just
  // takes those rows out of view (Gmail's own behavior). "Select all" only
  // ever touches the currently-VISIBLE (filtered) rows, never the full set.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

  const toggleGroup = (key) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Load the scribe-roled members for the active org.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        if (activeOrgId) {
          const res = await accountApi.org.listOrganizationMembers(activeOrgId);
          const members =
            res.data?.status === "success" && Array.isArray(res.data.data?.members)
              ? res.data.data.members
              : [];
          if (cancelled) return;
          setScribes(members.filter((m) => (m.roles || []).includes(SCRIBE_ROLE)));
        }
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeOrgId, scoreApi, accountApi]);

  // (Re)load the selected show's classes.
  const loadClasses = useCallback(
    async (id) => {
      if (!id) {
        setClasses([]);
        return;
      }
      const list = unwrap(await scoreApi.classes.listClasses(id));
      setClasses(list);
    },
    [scoreApi],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError(null);
        await loadClasses(activeShowId);
      } catch (err) {
        if (!cancelled) setError(err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeShowId, loadClasses]);

  // #760 — the show's judge roster (score-judge entity, #730), for the
  // per-section (technical/quality) judge picker below. Non-fatal: an empty
  // roster just leaves the picker with no options, same treatment as tests.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!activeShowId) {
        setJudges([]);
        return;
      }
      try {
        const list = unwrap(await scoreApi.judges.listJudges(activeShowId));
        if (!cancelled) setJudges(list || []);
      } catch {
        if (!cancelled) setJudges([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeShowId, scoreApi]);

  // Load the show's tests to detect young-horse T/Q classes (non-fatal).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!activeShowId) {
        setTestsById({});
        return;
      }
      try {
        const list = unwrap(await scoreApi.tests.listTests(activeShowId));
        if (cancelled) return;
        setTestsById(Object.fromEntries((list || []).map((tst) => [tst.id, tst])));
      } catch {
        if (!cancelled) setTestsById({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeShowId, scoreApi]);

  // #340/#862 — extracted into a shared hook (useClassesNeedingPlacings) so
  // this page's chip and the nav rail's Placings badge derive from the exact
  // same fetch + formula and can never disagree.
  const { classesNeedingPlacings, needsPlacingsCount } = useClassesNeedingPlacings(activeShowId);

  const isYoungHorseClass = useCallback(
    (c) => Boolean(c.testId) && testsById[c.testId]?.scoringModel === "young-horse-average",
    [testsById],
  );

  // #2252 — only classes ACTUALLY carrying #2251's testDisagreement flag
  // ever trigger the per-class ride fetch below (useTestDisagreementRideTests
  // fires zero requests for an empty list), so this stays cheap on the
  // overwhelmingly common case of no flagged classes on the show.
  const flaggedClassIds = useMemo(
    () => classes.filter((c) => c.testDisagreement?.flagged).map((c) => c.id),
    [classes],
  );
  const testDisagreementRideTestIds = useTestDisagreementRideTests(flaggedClassIds, scoreApi);

  const allJudges = useMemo(() => {
    const set = new Set();
    for (const c of classes) {
      for (const names of Object.values(c.judgeAssignments || {})) {
        for (const name of names || []) if (name) set.add(name);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [classes]);

  // #865 — a roster lookup miss (stale/removed assignment) is a data
  // problem to surface quietly, not a raw userId to print in place of a
  // name. Callers that want the raw id for support can still get it —
  // ClassDetailDrawer's stale-value MenuItems put it in a tooltip.
  const scribeLabel = useCallback(
    (userId) => {
      const m = scribes.find((s) => s.userId === userId);
      return m ? userDisplayName(m) || t("common.unknownUser") : t("common.unknownUser");
    },
    [scribes, t],
  );

  // #760 — label a score-judge roster id for the section-judge Selects below.
  // #865 — same quiet-placeholder treatment as scribeLabel above.
  const judgeLabel = useCallback(
    (judgeId) => {
      const j = judges.find((jj) => jj.id === judgeId);
      if (!j) return t("common.unknownJudge");
      return userDisplayName(j) || t("common.unknownJudge");
    },
    [judges, t],
  );

  // Assign (or clear) the scribe for a SINGLE class at a position (#530), reload.
  const assign = async (classId, position, userId) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassScribeAssignment(classId, {
        position,
        userId: userId || null,
      });
      await loadClasses(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #1055 — bulk scribe assignment over the current selection. `assignments`
  // is { [position]: "" (leave unchanged) | "__CLEAR__" | userId } from the
  // dialog. Reuses the exact SAME write as the single-class `assign` above
  // (scoreApi.classes.setClassScribeAssignment) looped per selected class ×
  // position — there's no batch endpoint — but only reloads once at the end
  // instead of once per call, and only touches positions that class actually
  // judges (positionsForClass), so a position irrelevant to a given class is
  // silently skipped rather than erroring or creating a bogus assignment.
  const bulkAssign = async (assignments) => {
    const entries = Object.entries(assignments).filter(([, v]) => v !== "");
    if (entries.length === 0) return;
    const targets = classes.filter((c) => selectedIds.has(c.id));
    try {
      setSaving(true);
      setError(null);
      for (const c of targets) {
        const relevant = new Set(positionsForClass(c));
        for (const [position, value] of entries) {
          if (!relevant.has(position)) continue;
          const userId = value === "__CLEAR__" ? null : value;
          await scoreApi.classes.setClassScribeAssignment(c.id, { position, userId });
        }
      }
      await loadClasses(activeShowId);
      setSelectedIds(new Set());
      setBulkDialogOpen(false);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #542 — persist a class's Technical/Quality judging topology, reload.
  const saveQualityJudging = async (classId, patch) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassQualityJudging(classId, {
        topology: patch.topology,
        qualityPosition: patch.qualityPosition ?? null,
        qualityJudge: patch.qualityJudge ?? null,
      });
      await loadClasses(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #760 — persist a class's per-section (technical/quality) judge
  // assignment, reload. Same save/reload shape as saveQualityJudging above;
  // values are score-judge roster ids (#759's API), not bare judge names.
  const saveSectionJudgeAssignments = async (classId, patch) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassSectionJudgeAssignments(classId, {
        technical: patch.technical ?? null,
        quality: patch.quality ?? null,
      });
      await loadClasses(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #570 — persist a class's fault time limit (seconds), reload. Same
  // save/reload shape as saveQualityJudging above.
  const saveFaultTimeLimit = async (classId, faultTimeLimitSeconds) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassFaultTimeLimit(classId, {
        faultTimeLimitSeconds,
      });
      await loadClasses(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #772 (workstream error-deductions, #771-774) — persist a class's
  // error-of-course/test deduction scheme, reload. Same save/reload shape as
  // saveFaultTimeLimit above.
  const saveErrorDeductionScheme = async (classId, errorDeductionScheme) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassErrorDeductionScheme(classId, {
        errorDeductionScheme,
      });
      await loadClasses(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #1783: persist a class's walk/trot-only flag, reload. Same save/reload
  // shape as saveErrorDeductionScheme above.
  const saveWalkTrotOnly = async (classId, walkTrotOnly) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassWalkTrotOnly(classId, {
        walkTrotOnly,
      });
      await loadClasses(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #2277: persist a class's DSHB kind override (null clears it back to the
  // className/fvdTestId guess), reload. Same save/reload shape as
  // saveWalkTrotOnly above.
  const saveDshbKindOverride = async (classId, dshbKindOverride) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassDshbKindOverride(classId, {
        dshbKindOverride,
      });
      await loadClasses(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #553 — filter → group → sort. Groups by day / ring / day+ring (or none),
  // sorted within each group by class number or name.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return classes.filter((c) => {
      // #591 — never show classes with no ring or no scheduled start time.
      if (!c.ring || !c.scheduledTime) return false;
      // #575/#593 — only classes that actually need a scribe (judged positions
      // with at least one unassigned) so the toggle visibly filters.
      if (noScribeOnly && !isMissingScribe(c)) return false;
      // #340 — only classes scored but not yet placed.
      if (needsPlacingsOnly && !classesNeedingPlacings.has(c.id)) return false;
      if (!q) return true;
      return `${classNumberLabel(c)} ${c.className ?? ""}`.toLowerCase().includes(q);
    });
  }, [classes, search, noScribeOnly, needsPlacingsOnly, classesNeedingPlacings]);

  const groups = useMemo(() => {
    const keyOf = (c) => {
      if (groupBy === "day") return classDay(c) || "";
      if (groupBy === "ring") return c.ring || "";
      if (groupBy === "day-ring") return [classDay(c), c.ring].filter(Boolean).join(" · ");
      return "";
    };
    const map = new Map();
    for (const c of filtered) {
      const k = keyOf(c);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(c);
    }
    // #572 — rows with no value for the active sort field sort to the END
    // (nulls last). compareByClassNumber already trails no-number classes;
    // the name comparator pushes blank names last instead of first.
    const byNameNullsLast = (a, b) => {
      const an = (a.className || "").trim();
      const bn = (b.className || "").trim();
      if (!an && !bn) return 0;
      if (!an) return 1;
      if (!bn) return -1;
      return an.localeCompare(bn);
    };
    // #573 — sort by scheduled start time, nulls (unscheduled) last.
    const byStartTimeNullsLast = (a, b) => {
      const at = (a.scheduledTime || "").trim();
      const bt = (b.scheduledTime || "").trim();
      if (!at && !bt) return 0;
      if (!at) return 1;
      if (!bt) return -1;
      return at.localeCompare(bt, undefined, { numeric: true });
    };
    const sortRows = (rows) => {
      if (sortBy === "name") return [...rows].sort(byNameNullsLast);
      if (sortBy === "time") return [...rows].sort(byStartTimeNullsLast);
      return sortByClassNumber(rows);
    };
    // #653 — day groups must sort chronologically, not alphabetically on the
    // rendered weekday label (classDay() = "Thu"/"Fri"/"Sat"; alphabetically
    // that's Fri < Sat < Thu, exactly backwards). Sort on classDayKey(), the
    // sortable YYYY-MM-DD slice of scheduledTime (#634) — never on the label
    // text. Every row in a "day"/"day-ring" group shares one scheduledTime day
    // (that's what grouped them), so the first row's key represents the group.
    // "day-ring" ties within the same day on the full key ("Thu · A" vs
    // "Thu · B"), which — since the day prefix matches — falls through to
    // comparing the ring suffix. Plain "ring" grouping is untouched: it still
    // sorts alphabetically on the ring label.
    const groupDayKey = (rows) => classDayKey(rows[0]) || "";
    const compareGroups = ([keyA, rowsA], [keyB, rowsB]) => {
      if (groupBy === "day" || groupBy === "day-ring") {
        const dayCmp = groupDayKey(rowsA).localeCompare(groupDayKey(rowsB));
        if (dayCmp !== 0) return dayCmp;
      }
      return String(keyA).localeCompare(String(keyB), undefined, { numeric: true });
    };
    return [...map.entries()]
      .sort(compareGroups)
      .map(([k, rows]) => [k, sortRows(rows)]);
  }, [filtered, groupBy, sortBy]);

  const groupHeader = (key) => {
    if (groupBy === "none") return null;
    if (groupBy === "ring") return key ? t("ring.ringLabel", { ring: key }) : t("ring.unassignedRing");
    if (groupBy === "day") return key || t("ring.noDay");
    return key || t("ring.unassignedRing"); // day-ring
  };

  const selectedClass = classes.find((c) => c.id === selectedClassId) ?? null;

  // #1055 — bulk-select derived state. `filtered` (not `groups`) is the
  // right "currently visible" set: groups only reshapes/buckets filtered
  // rows for display, a collapsed group's rows are still on-screen-relevant
  // for "select all visible" (they're one click away, not filtered out).
  const visibleIds = useMemo(() => filtered.map((c) => c.id), [filtered]);
  const visibleSelectedCount = visibleIds.filter((id) => selectedIds.has(id)).length;
  const allVisibleSelected = visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;
  const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected;

  const toggleSelectAllVisible = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of visibleIds) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };

  const toggleSelectOne = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const selectedClasses = useMemo(
    () => classes.filter((c) => selectedIds.has(c.id)),
    [classes, selectedIds],
  );

  // Union of judged positions across every selected class, in JUDGE_POSITIONS
  // order, for the bulk dialog's per-position pickers.
  const bulkPositions = useMemo(() => {
    const set = new Set();
    for (const c of selectedClasses) {
      for (const p of positionsForClass(c)) set.add(p);
    }
    return JUDGE_POSITIONS.filter((p) => set.has(p));
  }, [selectedClasses]);

  // #867 — tab title: once the URL points at a specific class (:classId,
  // #863's deep-linkable detail drawer), reflect that class's number/name so
  // an operator with several class tabs open can tell them apart; the plain
  // list title otherwise (including while `classes` is still loading, before
  // `selectedClass` can resolve).
  useDocumentTitle(
    selectedClassId && selectedClass
      ? t("title.class", { label: classNumberLabel(selectedClass) })
      : t("title.classes"),
  );

  if (loading || loadingShow) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom color="text.primary">
          {t("ring.classesTitle")}
        </Typography>
        <TableSkeleton rows={4} columns={2} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* #614 — match the loading-state heading + the "Classes" nav item so the
          title stays "Classes" from first paint through data load (no flip to
          the old "Ring → Scribe assignments"). */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Typography variant="h4" color="text.primary">
          {t("ring.classesTitle")}
        </Typography>
        {/* #340 — count of classes scored but not yet placed, visible without
            opening the filter below. */}
        {activeShowId && (
          <Chip
            size="small"
            color={needsPlacingsCount > 0 ? "warning" : "default"}
            label={t("ring.needsPlacingsCount", { count: needsPlacingsCount })}
            data-testid="needs-placings-count"
            clickable={needsPlacingsCount > 0}
            onClick={needsPlacingsCount > 0 ? () => navigate("/placings") : undefined}
            sx={
              needsPlacingsCount > 0
                ? { cursor: "pointer", "&:hover": { filter: "brightness(0.92)" } }
                : undefined
            }
          />
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || t("ring.loadError")}
        </Alert>
      )}

      {!activeShowId && <Typography color="text.secondary">{t("ring.noShows")}</Typography>}

      {scribes.length === 0 && activeShowId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("ring.noScribes")}
        </Alert>
      )}

      {activeShowId && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 2, maxWidth: 720 }}
          alignItems={{ sm: "center" }}
        >
          <TextField
            size="small"
            fullWidth
            placeholder={t("ring.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="class-group-label">{t("ring.groupBy")}</InputLabel>
            <Select
              labelId="class-group-label"
              label={t("ring.groupBy")}
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
            >
              <MenuItem value="none">{t("ring.groupNone")}</MenuItem>
              <MenuItem value="day">{t("ring.groupDay")}</MenuItem>
              <MenuItem value="ring">{t("ring.groupRing")}</MenuItem>
              <MenuItem value="day-ring">{t("ring.groupDayRing")}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="class-sort-label">{t("ring.sortBy")}</InputLabel>
            <Select
              labelId="class-sort-label"
              label={t("ring.sortBy")}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="number">{t("ring.sortNumber")}</MenuItem>
              <MenuItem value="name">{t("ring.sortName")}</MenuItem>
              <MenuItem value="time">{t("ring.sortTime")}</MenuItem>
            </Select>
          </FormControl>
          {/* #575 — filter to classes with no scribe assigned; combines with
              search + grouping + sort. */}
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={noScribeOnly}
                onChange={(e) => setNoScribeOnly(e.target.checked)}
              />
            }
            label={t("ring.filterNoScribe")}
          />
          {/* #340 — filter to classes that are scored but have no placings
              recorded; combines with search + grouping + sort + Missing scribe. */}
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={needsPlacingsOnly}
                onChange={(e) => setNeedsPlacingsOnly(e.target.checked)}
              />
            }
            label={t("ring.filterNeedsPlacings")}
          />
        </Stack>
      )}

      {/* #1055 — bulk-action toolbar: selected count on the left, the
          'Assign Scribes' button top-right, greyed out until 1+ rows are
          checked. Sits above the table so it stays visible regardless of
          grouping/collapse state. */}
      {activeShowId && groups.length > 0 && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1, minHeight: 36 }}
        >
          <Typography variant="body2" color="text.secondary" data-testid="bulk-selected-count">
            {selectedIds.size > 0 ? t("ring.bulkAssign.selectedCount", { count: selectedIds.size }) : ""}
          </Typography>
          <Button
            variant="contained"
            size="small"
            disabled={selectedIds.size === 0}
            onClick={() => setBulkDialogOpen(true)}
            data-testid="bulk-assign-scribes-button"
          >
            {t("ring.bulkAssign.assignButton")}
          </Button>
        </Stack>
      )}

      {activeShowId && groups.length === 0 && (
        <Typography color="text.secondary">{t("ring.noClasses")}</Typography>
      )}

      {/* #672 — a real MUI Table (matching ClassPlacings.jsx's pattern) so the
          number/name/scribe/time/day/ring columns line up down the page,
          instead of a List whose Stack of chips landed wherever the class
          name happened to end. Groups render as full-width rows spanning all
          columns; collapsing a group just omits its rows from this single
          TableBody, so column widths stay computed across every group. */}
      {activeShowId && groups.length > 0 && (
        <TableContainer data-testid="classes-table">
          <Table size="small">
            <TableHead>
              <TableRow>
                {/* #1055 — select-all checkbox, top-left of the table. Toggles
                    only the currently-visible/filtered rows (`visibleIds`),
                    never rows hidden by the active search/filters. */}
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected}
                    onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                    inputProps={{ "aria-label": t("ring.bulkAssign.selectAllVisible") }}
                    data-testid="select-all-classes"
                  />
                </TableCell>
                <TableCell>{t("ring.columns.number")}</TableCell>
                <TableCell>{t("ring.columns.name")}</TableCell>
                <TableCell>{t("ring.columns.scribe")}</TableCell>
                <TableCell align="right">{t("ring.columns.time")}</TableCell>
                <TableCell>{t("ring.columns.day")}</TableCell>
                <TableCell>{t("ring.columns.ring")}</TableCell>
                {/* #1062 — judge column added at the end so it doesn't shift
                    any of the existing positionally-indexed columns. */}
                <TableCell>{t("ring.columns.judge")}</TableCell>
                {/* #554 — FVD push-state icon column, added at the end for
                    the same reason as #1062's judge column above. */}
                <TableCell align="center">{t("ring.columns.fvdPush")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map(([key, rows]) => {
                const header = groupHeader(key);
                const isCollapsed = header && collapsedGroups.has(key);
                return (
                  <Fragment key={key || "__none__"}>
                    {header && (
                      // #574 — click the header to collapse/expand this group's rows.
                      <TableRow
                        hover
                        onClick={() => toggleGroup(key)}
                        sx={{ cursor: "pointer", userSelect: "none" }}
                        data-testid={`group-header-${key || "none"}`}
                      >
                        <TableCell colSpan={9} sx={{ borderBottom: isCollapsed ? "none" : undefined }}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <IconButton size="small" sx={{ p: 0 }} aria-label={t("ring.toggleGroup")}>
                              {isCollapsed ? (
                                <ChevronRightIcon fontSize="small" />
                              ) : (
                                <ExpandMoreIcon fontSize="small" />
                              )}
                            </IconButton>
                            <Typography variant="overline" color="text.secondary">
                              {header} ({rows.length})
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )}
                    {!isCollapsed &&
                      rows.map((c) => {
                        const summary = scribeSummary(c, scribeLabel);
                        const missing = missingScribePositions(c);
                        const judgePanel = judgeSummary(c);
                        return (
                          <TableRow
                            key={c.id}
                            hover
                            data-testid={`class-row-${c.id}`}
                            sx={{ cursor: "pointer" }}
                            onClick={() => navigate(`/classes/${c.id}`)}
                          >
                            {/* #1055 — row checkbox. stopPropagation so checking
                                a row doesn't also fire the row's own onClick
                                (which navigates into the class detail drawer). */}
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                size="small"
                                checked={selectedIds.has(c.id)}
                                onChange={(e) => toggleSelectOne(c.id, e.target.checked)}
                                inputProps={{
                                  "aria-label": t("ring.bulkAssign.selectRow", {
                                    label: classNumberLabel(c),
                                  }),
                                }}
                                data-testid={`select-class-${c.id}`}
                              />
                            </TableCell>
                            {/* #864 — shared ClassLink, real anchor semantics into
                                /classes/:classId (keyboard/cmd-click-able) on top of
                                the row's own onClick above. #673 keeps the number and
                                name in separate, non-duplicating columns, so each
                                passes its own text as `children` rather than the
                                component's default classNumberLabel() (which would
                                repeat the number in both cells). */}
                            <TableCell>
                              <ClassLink classId={c.id}>{c.classNumber ?? ""}</ClassLink>
                            </TableCell>
                            <TableCell sx={{ color: "text.primary" }}>
                              {/* #865 — a class missing its own name is a data
                                  problem to surface quietly, not the class's own
                                  id printed in place of a name. The id is still
                                  one click away (row onClick + the ClassLink
                                  href above), so no separate tooltip needed. */}
                              <ClassLink classId={c.id}>{c.className || t("common.unknownClass")}</ClassLink>
                              {/* #340 — badge on the row for a class that's scored
                                  but has no placings recorded, matching the #575
                                  no-scribe pattern. */}
                              {classesNeedingPlacings.has(c.id) && (
                                <Chip
                                  size="small"
                                  color="warning"
                                  label={t("ring.needsPlacingsBadge")}
                                  data-testid={`needs-placings-badge-${c.id}`}
                                  sx={{ ml: 1 }}
                                />
                              )}
                              {/* #2252 — class/ride test-disagreement flag from
                                  #2251, readable right here without opening the
                                  detail drawer (see TestDisagreementChip's own
                                  doc comment for why no "fix it here" control). */}
                              <TestDisagreementChip
                                testDisagreement={c.testDisagreement}
                                classTestId={c.testId}
                                rideTestIds={testDisagreementRideTestIds[c.id]}
                                testsById={testsById}
                              />
                            </TableCell>
                            <TableCell data-testid={`scribe-cell-${c.id}`}>
                              {summary}
                              {missing.length > 0 && (
                                <>
                                  {summary ? " · " : ""}
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                    data-testid={`scribe-missing-${c.id}`}
                                  >
                                    {t("ring.missingPositions", { positions: missing.join(", ") })}
                                  </Typography>
                                </>
                              )}
                            </TableCell>
                            {/* #573 — scheduled start time; right-aligned so times read
                                as a clean vertical column when Sort by is Start time. */}
                            <TableCell align="right">
                              {formatStartTime(c.scheduledTime) || t("ring.noTime")}
                            </TableCell>
                            <TableCell>{classDay(c) && <Chip size="small" label={classDay(c)} />}</TableCell>
                            <TableCell>
                              {c.ring && <Chip size="small" label={t("ring.ringLabel", { ring: c.ring })} />}
                            </TableCell>
                            {/* #1062 — FVD-reconciled judge panel for the class,
                                e.g. "C: Jane Smith · H: Bob Jones". A class with
                                no judge panel yet (or none of FVD's positions
                                resolved a name) shows a muted empty state
                                rather than a blank cell. */}
                            <TableCell data-testid={`judge-cell-${c.id}`}>
                              {judgePanel || (
                                <Typography component="span" variant="body2" color="text.secondary">
                                  {t("ring.noJudge")}
                                </Typography>
                              )}
                            </TableCell>
                            {/* #554 — FVD-push status icon: gray/green/red
                                Fox Village logo reflecting the class's
                                aggregate fvdPushState from score-api. */}
                            <TableCell align="center">
                              <FvdPushStatusIcon fvdPushState={c.fvdPushState} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ClassDetailDrawer
        open={!!selectedClass}
        onClose={() => navigate("/classes")}
        classItem={selectedClass}
        scribes={scribes}
        saving={saving}
        positionsForClass={positionsForClass}
        scribeLabel={scribeLabel}
        onAssign={assign}
        isYoungHorse={isYoungHorseClass}
        allJudges={allJudges}
        onSaveQualityJudging={saveQualityJudging}
        onSaveFaultTimeLimit={saveFaultTimeLimit}
        onSaveErrorDeductionScheme={saveErrorDeductionScheme}
        onSaveWalkTrotOnly={saveWalkTrotOnly}
        onSaveDshbKindOverride={saveDshbKindOverride}
        judges={judges}
        judgeLabel={judgeLabel}
        onSaveSectionJudgeAssignments={saveSectionJudgeAssignments}
      />

      <BulkAssignScribesDialog
        open={bulkDialogOpen}
        onClose={() => setBulkDialogOpen(false)}
        classCount={selectedClasses.length}
        positions={bulkPositions}
        scribes={scribes}
        saving={saving}
        error={error}
        onSubmit={bulkAssign}
      />
    </Box>
  );
}
